import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Check,
  Download,
  Eye,
  Layers,
  Loader2,
  Menu,
  MessageCircle,
  RefreshCw,
  ShoppingBag,
  Store,
  X,
} from 'lucide-react';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { useUIStore } from '@/store/uiStore';
import { authApi, ebayApi, shopifyApi, storefrontOrdersApi, whatsappApi } from '@/services/api';

export type ChannelId =
  | 'all'
  | 'store'
  | 'whatsapp'
  | 'shopify'
  | 'amazon'
  | 'ebay'
  | 'onbuy'
  | 'meta'
  | 'tiktok';

type UnifiedOrder = {
  key: string;
  source: 'store' | 'whatsapp' | 'shopify' | 'ebay';
  name: string;
  displayId: string;
  customer: string;
  contact: string;
  total: number;
  currency: string;
  status: string;
  creation: string;
  canAccept: boolean;
  canReject: boolean;
  raw: any;
};

const CHANNELS: {
  id: ChannelId;
  label: string;
  requires?: 'whatsapp' | 'shopify' | 'ebay';
  comingSoon?: boolean;
}[] = [
  { id: 'all', label: 'All' },
  { id: 'store', label: 'Online store' },
  { id: 'whatsapp', label: 'WhatsApp', requires: 'whatsapp' },
  { id: 'shopify', label: 'Shopify', requires: 'shopify' },
  { id: 'ebay', label: 'eBay', requires: 'ebay' },
  { id: 'amazon', label: 'Amazon', comingSoon: true },
  { id: 'onbuy', label: 'OnBuy', comingSoon: true },
  { id: 'meta', label: 'Meta', comingSoon: true },
  { id: 'tiktok', label: 'TikTok', comingSoon: true },
];

const SOURCE_META: Record<
  UnifiedOrder['source'],
  { label: string; className: string; icon: typeof Store }
> = {
  store: { label: 'Online store', className: 'bg-teal-50 text-teal-800', icon: Store },
  whatsapp: { label: 'WhatsApp', className: 'bg-emerald-50 text-emerald-800', icon: MessageCircle },
  shopify: { label: 'Shopify', className: 'bg-green-50 text-green-800', icon: ShoppingBag },
  ebay: { label: 'eBay', className: 'bg-blue-50 text-blue-800', icon: ShoppingBag },
};

function shopifyOpen(status: string) {
  return status === 'New' || status === 'Mapped';
}

function channelOrderOpen(status: string) {
  return status === 'New' || status === 'Mapped';
}

export default function ChannelOrdersPage() {
  const { toggleSidebar } = useUIStore();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [detail, setDetail] = useState<{ source: UnifiedOrder['source']; data: any } | null>(null);

  const { data: companyProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => (await authApi.getCompanyProfile()).data.message,
    staleTime: 60_000,
  });

  const whatsappEnabled =
    (companyProfile?.enable_whatsapp_shop === true || companyProfile?.enable_whatsapp_shop === 1) &&
    Boolean(companyProfile?.allow_whatsapp);
  const shopifyEnabled =
    (companyProfile?.enable_shopify === true || companyProfile?.enable_shopify === 1) &&
    Boolean(companyProfile?.allow_shopify);
  const ebayEnabled =
    (companyProfile?.enable_ebay === true || companyProfile?.enable_ebay === 1) &&
    Boolean(companyProfile?.allow_ebay);

  const available = useMemo(
    () =>
      CHANNELS.filter((c) => {
        if (c.requires === 'whatsapp' && !whatsappEnabled) return false;
        if (c.requires === 'shopify' && !shopifyEnabled) return false;
        if (c.requires === 'ebay' && !ebayEnabled) return false;
        return true;
      }),
    [whatsappEnabled, shopifyEnabled, ebayEnabled],
  );

  const raw = (params.get('channel') || 'all') as ChannelId;
  const channel: ChannelId = available.some((c) => c.id === raw) ? raw : 'all';

  const setChannel = (id: ChannelId) => {
    const next = new URLSearchParams(params);
    if (id === 'all') next.delete('channel');
    else next.set('channel', id);
    setParams(next, { replace: true });
  };

  const fetchStore = channel === 'all' || channel === 'store';
  const fetchWa = whatsappEnabled && (channel === 'all' || channel === 'whatsapp');
  const fetchShopify = shopifyEnabled && (channel === 'all' || channel === 'shopify');
  const fetchEbay = ebayEnabled && (channel === 'all' || channel === 'ebay');

  // Map UI status filter per channel API quirks
  const storeStatus = statusFilter === 'Open' ? 'Pending' : statusFilter || undefined;
  const waStatus = statusFilter === 'Open' || statusFilter === 'Accepted' ? 'Pending' : statusFilter || undefined;
  const shopifyStatus =
    statusFilter === 'Pending' || statusFilter === 'Accepted' || statusFilter === 'Open'
      ? 'Open'
      : statusFilter || undefined;
  const ebayStatus =
    statusFilter === 'Open' || statusFilter === 'Pending' || statusFilter === 'Accepted'
      ? 'New'
      : statusFilter || undefined;

  const results = useQueries({
    queries: [
      {
        queryKey: ['storefrontOrders', 'unified', storeStatus],
        queryFn: async () => {
          const res = await storefrontOrdersApi.list({ status: storeStatus, limit: 100 });
          return (res.data.message as any[]) || [];
        },
        enabled: fetchStore,
        refetchInterval: 20000,
      },
      {
        queryKey: ['whatsappOrders', 'unified', waStatus],
        queryFn: async () => {
          const res = await whatsappApi.getOrders({ status: waStatus, limit: 100 });
          return (res.data.message?.orders as any[]) || [];
        },
        enabled: fetchWa,
        refetchInterval: 30000,
      },
      {
        queryKey: ['shopifyOrders', 'unified', shopifyStatus],
        queryFn: async () => {
          const res = await shopifyApi.getOrders({ status: shopifyStatus, limit: 100 });
          return (res.data.message?.orders as any[]) || [];
        },
        enabled: fetchShopify,
        refetchInterval: 30000,
      },
      {
        queryKey: ['ebayOrders', 'unified', ebayStatus],
        queryFn: async () => {
          const res = await ebayApi.listChannelOrders({ status: ebayStatus, limit: 100 });
          return (res.data.message?.orders as any[]) || [];
        },
        enabled: fetchEbay,
        refetchInterval: 30000,
      },
    ],
  });

  const [storeQ, waQ, shopifyQ, ebayQ] = results;
  const isLoading = results.some((r) => r.isLoading && r.fetchStatus !== 'idle');
  const isFetching = results.some((r) => r.isFetching);

  const orders: UnifiedOrder[] = useMemo(() => {
    const rows: UnifiedOrder[] = [];

    if (fetchStore) {
      for (const o of storeQ.data || []) {
        rows.push({
          key: `store:${o.name}`,
          source: 'store',
          name: o.name,
          displayId: o.name,
          customer: o.customer_name || '—',
          contact: o.customer_mobile || '',
          total: Number(o.grand_total || 0),
          currency: o.currency || '',
          status: o.status,
          creation: o.creation || o.modified || '',
          canAccept: o.status === 'Pending',
          canReject: o.status === 'Pending',
          raw: o,
        });
      }
    }

    if (fetchWa) {
      for (const o of waQ.data || []) {
        rows.push({
          key: `whatsapp:${o.name}`,
          source: 'whatsapp',
          name: o.name,
          displayId: o.name,
          customer: o.customer_name || '—',
          contact: o.customer_mobile || '',
          total: Number(o.grand_total || 0),
          currency: o.currency || '',
          status: o.status,
          creation: o.creation || '',
          canAccept: o.status === 'Pending',
          canReject: o.status === 'Pending',
          raw: o,
        });
      }
    }

    if (fetchShopify) {
      for (const o of shopifyQ.data || []) {
        const open = shopifyOpen(o.status);
        rows.push({
          key: `shopify:${o.name}`,
          source: 'shopify',
          name: o.name,
          displayId: o.shopify_order_name || o.name,
          customer: o.customer_name || '—',
          contact: o.customer_mobile || o.customer_email || '',
          total: Number(o.grand_total || 0),
          currency: o.currency || '',
          status: o.status,
          creation: o.creation || '',
          canAccept: open || o.status === 'Rejected',
          canReject: open,
          raw: o,
        });
      }
    }

    if (fetchEbay) {
      for (const o of ebayQ.data || []) {
        const open = channelOrderOpen(o.status);
        rows.push({
          key: `ebay:${o.name}`,
          source: 'ebay',
          name: o.name,
          displayId: o.external_order_number || o.external_order_id || o.name,
          customer: o.customer_name || '—',
          contact: o.customer_email || '',
          total: Number(o.grand_total || 0),
          currency: o.currency || 'GBP',
          status: o.status,
          creation: o.creation || '',
          canAccept: open || o.status === 'Rejected',
          canReject: open,
          raw: o,
        });
      }
    }

    rows.sort((a, b) => String(b.creation).localeCompare(String(a.creation)));
    return rows;
  }, [fetchStore, fetchWa, fetchShopify, fetchEbay, storeQ.data, waQ.data, shopifyQ.data, ebayQ.data]);

  const invalidateAll = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['storefrontOrders'] }),
      qc.invalidateQueries({ queryKey: ['whatsappOrders'] }),
      qc.invalidateQueries({ queryKey: ['shopifyOrders'] }),
      qc.invalidateQueries({ queryKey: ['ebayOrders'] }),
    ]);
  };

  const acceptMutation = useMutation({
    mutationFn: async (row: UnifiedOrder) => {
      if (row.source === 'store') return storefrontOrdersApi.accept(row.name);
      if (row.source === 'whatsapp') return whatsappApi.confirmOrder(row.name);
      if (row.source === 'ebay') return ebayApi.confirmOrder(row.name);
      return shopifyApi.confirmOrder(row.name);
    },
    onSuccess: async (res, row) => {
      const msg = res.data?.message;
      if ((row.source === 'shopify' || row.source === 'ebay') && msg?.needs_resolution) {
        toast.error(msg.message || 'Map unmapped lines first — open the order for details.');
        try {
          if (row.source === 'ebay') {
            const d = await ebayApi.getOrder(row.name);
            setDetail({ source: 'ebay', data: d.data.message });
          } else {
            const d = await shopifyApi.getOrder(row.name);
            setDetail({ source: 'shopify', data: d.data.message });
          }
        } catch {
          /* ignore */
        }
        return;
      }
      const invoice = msg?.invoice;
      toast.success(invoice ? `Accepted — invoice ${invoice}` : 'Order accepted');
      setDetail(null);
      // SD-UK-024: drop accepted row immediately, then refetch lists
      const qk =
        row.source === 'store'
          ? ['storefrontOrders']
          : row.source === 'whatsapp'
            ? ['whatsappOrders']
            : row.source === 'ebay'
              ? ['ebayOrders']
              : ['shopifyOrders'];
      qc.setQueriesData({ queryKey: qk }, (old: any) => {
        if (!old) return old;
        const list = old?.message ?? old;
        if (Array.isArray(list)) {
          const next = list.filter((o: any) => o.name !== row.name);
          return old?.message !== undefined ? { ...old, message: next } : next;
        }
        if (Array.isArray(old?.orders)) {
          return { ...old, orders: old.orders.filter((o: any) => o.name !== row.name) };
        }
        return old;
      });
      await invalidateAll();
      await Promise.all([
        qc.refetchQueries({ queryKey: ['storefrontOrders'] }),
        qc.refetchQueries({ queryKey: ['whatsappOrders'] }),
        qc.refetchQueries({ queryKey: ['shopifyOrders'] }),
        qc.refetchQueries({ queryKey: ['ebayOrders'] }),
      ]);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || err.message),
  });

  const rejectMutation = useMutation({
    mutationFn: async (row: UnifiedOrder) => {
      if (row.source === 'store') return storefrontOrdersApi.reject(row.name);
      if (row.source === 'whatsapp') return whatsappApi.rejectOrder(row.name);
      if (row.source === 'ebay') return ebayApi.rejectOrder(row.name);
      return shopifyApi.rejectOrder(row.name);
    },
    onSuccess: async (_res, row) => {
      toast.success('Order rejected');
      setDetail(null);
      // SD-UK-024: mirror accept — drop rejected row immediately
      const qk =
        row.source === 'store'
          ? ['storefrontOrders']
          : row.source === 'whatsapp'
            ? ['whatsappOrders']
            : row.source === 'ebay'
              ? ['ebayOrders']
              : ['shopifyOrders'];
      qc.setQueriesData({ queryKey: qk }, (old: any) => {
        if (!old) return old;
        const list = old?.message ?? old;
        if (Array.isArray(list)) {
          const next = list.filter((o: any) => o.name !== row.name);
          return old?.message !== undefined ? { ...old, message: next } : next;
        }
        if (Array.isArray(old?.orders)) {
          return { ...old, orders: old.orders.filter((o: any) => o.name !== row.name) };
        }
        return old;
      });
      await invalidateAll();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || err.message),
  });

  const fetchShopifyMutation = useMutation({
    mutationFn: () => shopifyApi.fetchOrders(25),
    onSuccess: (res) => {
      toast.success(`Fetched ${res.data.message?.fetched ?? 0} Shopify orders`);
      void invalidateAll();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || err.message),
  });

  const fetchEbayMutation = useMutation({
    mutationFn: () => ebayApi.fetchOrders(50),
    onSuccess: (res) => {
      toast.success(`Fetched ${res.data.message?.fetched ?? res.data.message?.count ?? 0} eBay orders`);
      void invalidateAll();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || err.message),
  });

  const openDetail = async (row: UnifiedOrder) => {
    try {
      if (row.source === 'store') {
        const res = await storefrontOrdersApi.get(row.name);
        setDetail({ source: 'store', data: res.data.message });
      } else if (row.source === 'whatsapp') {
        const res = await whatsappApi.getOrder(row.name);
        setDetail({ source: 'whatsapp', data: res.data.message });
      } else if (row.source === 'ebay') {
        const res = await ebayApi.getOrder(row.name);
        setDetail({ source: 'ebay', data: res.data.message });
      } else {
        const res = await shopifyApi.getOrder(row.name);
        setDetail({ source: 'shopify', data: res.data.message });
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const refresh = () => {
    void Promise.all(results.map((r) => r.refetch()));
  };

  const comingSoon = available.find((c) => c.id === channel)?.comingSoon;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <TrialBanner />
        <header className="bg-white border-b border-gray-200 px-4 py-3 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={toggleSidebar} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Layers className="w-5 h-5 text-teal-700" />
                Marketplace Orders
              </h1>
              <p className="text-sm text-gray-500">
                Store, WhatsApp and marketplace orders in one list — filter by source
              </p>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field text-sm w-36"
            >
              <option value="">All statuses</option>
              <option value="Open">Open / Pending</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
            </select>
            {shopifyEnabled && (channel === 'all' || channel === 'shopify') && (
              <button
                type="button"
                onClick={() => fetchShopifyMutation.mutate()}
                disabled={fetchShopifyMutation.isPending}
                className="btn-secondary flex items-center gap-1.5 text-sm"
                title="Fetch Shopify orders"
              >
                {fetchShopifyMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Fetch Shopify</span>
              </button>
            )}
            {ebayEnabled && (channel === 'all' || channel === 'ebay') && (
              <button
                type="button"
                onClick={() => fetchEbayMutation.mutate()}
                disabled={fetchEbayMutation.isPending}
                className="btn-secondary flex items-center gap-1.5 text-sm"
                title="Fetch eBay orders"
              >
                {fetchEbayMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Fetch eBay</span>
              </button>
            )}
            <button type="button" onClick={refresh} className="btn-secondary flex items-center gap-1.5">
              {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {available.map((c) => {
              const active = channel === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setChannel(c.id)}
                  disabled={!!c.comingSoon}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                    active
                      ? 'bg-teal-700 text-white'
                      : c.comingSoon
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-teal-600'
                  }`}
                >
                  {c.label}
                  {c.comingSoon ? ' · Soon' : ''}
                </button>
              );
            })}
          </div>
        </header>

        <div className="flex-1 min-h-0 overflow-auto p-4">
          {comingSoon ? (
            <div className="bg-white border border-dashed border-gray-200 rounded-xl p-10 text-center text-gray-500">
              <p className="font-medium text-gray-800">Integration coming soon</p>
              <p className="text-sm mt-1">This channel will appear in All Orders when connected.</p>
            </div>
          ) : isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-gray-700 border-b">
                    <tr>
                      <th className="px-4 py-3">Source</th>
                      <th className="px-4 py-3">Order</th>
                      <th className="px-4 py-3">Customer</th>
                      <th className="px-4 py-3 text-right">Total</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {orders.map((o) => {
                      const meta = SOURCE_META[o.source];
                      const Icon = meta.icon;
                      return (
                        <tr key={o.key} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${meta.className}`}
                            >
                              <Icon className="w-3.5 h-3.5" />
                              {meta.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900">{o.displayId}</td>
                          <td className="px-4 py-3">
                            {o.customer}
                            {o.contact ? <div className="text-xs text-gray-500">{o.contact}</div> : null}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold">
                            {o.total.toFixed(2)} {o.currency}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                o.status === 'Pending' ||
                                shopifyOpen(o.status) ||
                                (o.source === 'ebay' && channelOrderOpen(o.status))
                                  ? 'bg-amber-100 text-amber-800'
                                  : o.status === 'Confirmed' || o.status === 'Accepted'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : o.status === 'Rejected'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {(o.source === 'shopify' || o.source === 'ebay') &&
                              (shopifyOpen(o.status) || channelOrderOpen(o.status))
                                ? 'Open'
                                : o.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => openDetail(o)}
                                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {o.canAccept && (
                                <button
                                  type="button"
                                  disabled={acceptMutation.isPending}
                                  onClick={() => acceptMutation.mutate(o)}
                                  className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                                  title="Accept"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                              )}
                              {o.canReject && (
                                <button
                                  type="button"
                                  disabled={rejectMutation.isPending}
                                  onClick={() => rejectMutation.mutate(o)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                  title="Reject"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!orders.length && (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                          No orders for this filter
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {detail && (
          <div
            className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setDetail(null)}
          >
            <div
              className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {SOURCE_META[detail.source].label}
                  </p>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {detail.data.shopify_order_name ||
                      detail.data.external_order_number ||
                      detail.data.external_order_id ||
                      detail.data.name}
                  </h3>
                </div>
                <button type="button" onClick={() => setDetail(null)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {detail.data.customer_name}
                {detail.data.customer_mobile ? ` · ${detail.data.customer_mobile}` : ''}
                {detail.data.fulfillment_type ? ` · ${detail.data.fulfillment_type}` : ''}
              </p>
              <p className="text-sm mt-2">
                Status: <span className="font-medium">{detail.data.status}</span>
                {detail.data.pos_invoice ? (
                  <>
                    {' '}
                    · Invoice: <span className="font-medium">{detail.data.pos_invoice}</span>
                  </>
                ) : null}
              </p>
              <ul className="mt-4 divide-y text-sm border rounded-lg overflow-hidden">
                {(detail.data.items || []).map((i: any, idx: number) => (
                  <li key={idx} className="px-3 py-2 flex justify-between gap-2">
                    <span>
                      {i.qty ?? i.quantity} × {i.item_name}
                    </span>
                    <span className="shrink-0">{i.amount ?? Number(i.rate || 0).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-right font-bold mt-3">
                {detail.data.currency} {detail.data.grand_total}
              </p>
              {(detail.data.status === 'Pending' ||
                shopifyOpen(detail.data.status) ||
                channelOrderOpen(detail.data.status) ||
                detail.data.status === 'Rejected') && (
                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    className="btn-primary flex-1"
                    disabled={acceptMutation.isPending}
                    onClick={() =>
                      acceptMutation.mutate({
                        key: `${detail.source}:${detail.data.name}`,
                        source: detail.source,
                        name: detail.data.name,
                        displayId: detail.data.name,
                        customer: '',
                        contact: '',
                        total: 0,
                        currency: '',
                        status: detail.data.status,
                        creation: '',
                        canAccept: true,
                        canReject: true,
                        raw: detail.data,
                      })
                    }
                  >
                    Accept
                  </button>
                  {(detail.data.status === 'Pending' ||
                    shopifyOpen(detail.data.status) ||
                    channelOrderOpen(detail.data.status)) && (
                    <button
                      type="button"
                      className="btn-secondary flex-1"
                      disabled={rejectMutation.isPending}
                      onClick={() =>
                        rejectMutation.mutate({
                          key: `${detail.source}:${detail.data.name}`,
                          source: detail.source,
                          name: detail.data.name,
                          displayId: detail.data.name,
                          customer: '',
                          contact: '',
                          total: 0,
                          currency: '',
                          status: detail.data.status,
                          creation: '',
                          canAccept: true,
                          canReject: true,
                          raw: detail.data,
                        })
                      }
                    >
                      Reject
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
